import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Bot, LogOut, ShieldCheck, Loader2, Users, CreditCard, Megaphone, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { PasswordInput } from "@/components/PasswordInput";

const input = "w-full rounded-xl border border-[#27272A] bg-[#09090B] px-4 py-3 text-base text-[#FAF9F6] outline-none focus:border-[#D4AF37]";

export function AdminGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
}

function AdminLogin() {
  const { login, logout, user } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), pw);
      if (u.role !== "admin") {
        await logout();
        toast.error("Questo account non è admin");
      }
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 space-y-3" data-testid="admin-login-form">
      {user && <p className="text-sm text-[#FCA5A5]">Sei dentro come {user.email}, che non è admin. Accedi con l'account admin.</p>}
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email admin" autoComplete="username" data-testid="admin-login-email" className={input} />
      <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} testId="admin-login-password" placeholder="Password" minLength={1} className={input} />
      <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm uppercase tracking-wide">
        {busy && <Loader2 size={16} className="animate-spin" />} Entra
      </button>
    </form>
  );
}

function Tile({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex items-center gap-4 rounded-2xl border border-[#27272A] bg-[#121215] p-5 transition-colors hover:border-[#D4AF37]/60">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#D4AF37]/12 text-[#D4AF37]"><Icon size={24} /></span>
      <span>
        <span className="block text-lg font-semibold text-[#FAF9F6]">{title}</span>
        <span className="block text-sm text-[#8A8E96]">{desc}</span>
      </span>
    </Link>
  );
}

export default function AdminHome() {
  const { user, logout } = useAuth();
  if (user === null) return <Loader />;
  const isAdmin = user && user.role === "admin";

  return (
    <div className="min-h-screen px-5 pt-10 pb-16" data-testid="admin-home">
      <div className="flex items-center gap-2 text-[#D4AF37]"><ShieldCheck size={20} /><span className="eyebrow">Area riservata</span></div>
      <h1 className="font-serif-el mt-2 text-4xl font-semibold text-[#FAF9F6]">Admin Coach2Go</h1>
      {!isAdmin ? (
        <AdminLogin />
      ) : (
        <>
          <div className="mt-8 space-y-3">
            <Tile to="/admin/iscritti" icon={Users} title="Iscritti" desc="Elenco, modifica, accesso gratuito, Excel" testid="admin-tile-iscritti" />
            <Tile to="/admin/comunicazioni" icon={Megaphone} title="Comunicazioni" desc="Email, avvisi nell'app e notifiche" testid="admin-tile-comunicazioni" />
            <Tile to="/admin/contenuti" icon={BookOpen} title="Contenuti settimane" desc="Rileggi i testi di Settimana 2 e 3" testid="admin-tile-contenuti" />
            <Tile to="/admin/campagne" icon={BarChart3} title="Campagne" desc="Visite, iscritti, prove e Meta Pixel" testid="admin-tile-campagne" />
            <Tile to="/admin/coach" icon={Bot} title="Coach Mino" desc="Regole, limiti e interruttore" testid="admin-tile-coach" />
            <Tile to="/admin/versioni" icon={Bot} title="Versioni dei testi" desc="Cronologia e ritorno a una versione" testid="admin-tile-versions" />
            <Tile to="/admin/stripe" icon={CreditCard} title="Stripe" desc="Account collegato e codici sconto" testid="admin-tile-stripe" />
          </div>
          <button onClick={logout} data-testid="admin-logout" className="mt-8 inline-flex items-center gap-2 text-sm text-[#8A8E96] hover:text-[#FAF9F6]">
            <LogOut size={16} /> Esci da {user.email}
          </button>
        </>
      )}
    </div>
  );
}
